/**
 * Single place — stages-only Leaflet map.
 *
 * Reads stage data from `data-stages` JSON on #place-stages-map.
 * Clicking a stage marker scrolls the page to its section (#stage-{slug}).
 * Hovering a stage section title pans the map to that marker.
 */
import L from 'leaflet';

const STAGE_SIZE   = 64;
const ARROW_HEIGHT = 12;

export function initPlaceStagesMap() {
	const container = document.getElementById( 'place-stages-map' );
	if ( ! container ) return;

	const stages = JSON.parse( container.dataset.stages || '[]' );
	if ( ! stages.length ) return;

	const map = L.map( container, {
		scrollWheelZoom: false,
		zoomControl: true,
	} );

	L.tileLayer( 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
		attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
		maxZoom: 19,
	} ).addTo( map );

	const markerByAnchor = new Map();
	const points         = [];

	stages.forEach( ( stage ) => {
		const { lat, lng, title, thumbnail, anchor } = stage;

		const thumbHtml = thumbnail
			? `<img src="${ thumbnail }" alt="${ escAttr( title ) }" loading="lazy" class="stage-marker__img">`
			: `<span class="stage-marker__fallback">${ escHtml( title ) }</span>`;

		const icon = L.divIcon( {
			className:  'stage-marker',
			html:       `<span class="stage-marker__square">${ thumbHtml }</span><span class="stage-marker__arrow" aria-hidden="true"></span>`,
			iconSize:   [ STAGE_SIZE, STAGE_SIZE + ARROW_HEIGHT ],
			iconAnchor: [ STAGE_SIZE / 2, STAGE_SIZE + ARROW_HEIGHT ], // tip = center bottom
		} );

		const marker = L.marker( [ lat, lng ], { icon } );

		marker.on( 'mouseover', () => marker.getElement()?.classList.add( 'is-hovered' ) );
		marker.on( 'mouseout',  () => marker.getElement()?.classList.remove( 'is-hovered' ) );

		marker.on( 'click', () => {
			const target = document.getElementById( anchor );
			if ( target ) {
				target.scrollIntoView( { behavior: 'smooth', block: 'start' } );
			}
		} );

		marker.addTo( map );
		markerByAnchor.set( anchor, marker );
		points.push( [ lat, lng ] );
	} );

	// ── Fit map to all stage markers ───────────────────────────────────────────

	if ( points.length === 1 ) {
		map.setView( points[ 0 ], 17 );
	} else {
		map.fitBounds( L.latLngBounds( points ), { padding: [ 60, 60 ] } );
	}

	// ── Sync: hovering a stage section highlights its marker ──────────────────

	document.querySelectorAll( '.place-stage' ).forEach( ( section ) => {
		const anchor = section.id;
		const marker = markerByAnchor.get( anchor );
		if ( ! marker ) return;

		section.addEventListener( 'mouseenter', () => {
			marker.getElement()?.classList.add( 'is-hovered' );
		} );
		section.addEventListener( 'mouseleave', () => {
			marker.getElement()?.classList.remove( 'is-hovered' );
		} );
	} );

	// ── Utilities ──────────────────────────────────────────────────────────────

	function escHtml( str ) {
		return String( str )
			.replace( /&/g, '&amp;' )
			.replace( /</g, '&lt;' )
			.replace( />/g, '&gt;' )
			.replace( /"/g, '&quot;' );
	}

	function escAttr( str ) {
		return escHtml( str ).replace( /'/g, '&#39;' );
	}
}
